import { memo, useState, useEffect } from "react";
import { formatCurrency } from "@/lib/utils";

type LiquidationPriceDisplayProps = {
  // Backend-calculated prices (authoritative, includes funding)
  longLiqPrice: number;
  shortLiqPrice: number;
  quotePrecision: number;
  compact?: boolean;
  isLoading?: boolean;
};

/**
 * Display ESTIMATED liquidation prices from backend
 * Backend calculation includes cumulative funding payments
 * 
 * Industry standard: Backend is source of truth for risk metrics
 * - Accurate for new positions
 * - Accounts for funding payments (every 8 hours)
 * - Keeps previous value while loading to prevent flicker
 */
export const LiquidationPriceDisplay = memo(function LiquidationPriceDisplay({
  longLiqPrice,
  shortLiqPrice,
  quotePrecision,
  compact = false,
  isLoading = false,
}: LiquidationPriceDisplayProps) {
  // Keep track of last valid values to prevent flashing during loading
  const [displayLong, setDisplayLong] = useState(longLiqPrice);
  const [displayShort, setDisplayShort] = useState(shortLiqPrice);
  
  // Only update display values when we get new valid data
  useEffect(() => {
    if (longLiqPrice > 0) {
      setDisplayLong(longLiqPrice);
    }
  }, [longLiqPrice]);
  
  useEffect(() => {
    if (shortLiqPrice > 0) {
      setDisplayShort(shortLiqPrice);
    }
  }, [shortLiqPrice]);
  
  // Format display strings - use cached values, never show "..."
  const longDisplay = displayLong > 0 
    ? `$${formatCurrency(displayLong, quotePrecision)}` 
    : "—";
  
  const shortDisplay = displayShort > 0 
    ? `$${formatCurrency(displayShort, quotePrecision)}` 
    : "—";
  
  // Compact version for mobile
  if (compact) {
    return (
      <span className="font-medium text-[10px]">
        <span className="text-buy">{longDisplay}</span>
        <span className="text-muted-foreground"> / </span>
        <span className="text-sell">{shortDisplay}</span>
      </span>
    );
  }
  
  // Desktop version with subtle loading indicator (opacity only, no content change)
  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      <div className={`flex flex-col gap-0 p-1 bg-bid rounded transition-opacity duration-200 ${isLoading ? "opacity-70" : "opacity-100"}`}>
        <span className="text-muted-foreground text-[10px]">Est. Liq. Price</span>
        <span className="font-medium text-foreground/80 text-xs">
          {longDisplay}
        </span>
      </div>
      <div className={`flex flex-col gap-0 p-1 bg-ask rounded transition-opacity duration-200 ${isLoading ? "opacity-70" : "opacity-100"}`}>
        <span className="text-muted-foreground text-[10px]">Est. Liq. Price</span>
        <span className="font-medium text-foreground/80 text-xs">
          {shortDisplay}
        </span>
      </div>
    </div>
  );
});
